/******************************************************************************* 
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for any purpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { Fragment, useState } from "react";

//@Packages
import { InfoIcon } from "@forge/common";
import { useTranslation } from "react-i18next";

//@Components
import Toast from "../../reusablecomponents/toast";
import ConfirmationModal from "../../reusablecomponents/confirmationmodal";

//@Models
import { ActionModalProps } from "../../models/devicelist";

//@Services
import { useActivateDeviceMutation, useDeactivateDeviceMutation } from "../../services/api";

//@Utils
import { LOCALE_CONST } from "../../utils/constants";
import { gatewayErrorStatusMessages } from "../../utils/utility";

//@Styles
import "./index.scss";

//@Functional Component
const ActionModal: React.FC<ActionModalProps> = props => {
  const { t } = useTranslation();

  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  const [activateDevice] = useActivateDeviceMutation();
  const [deactivateDevice] = useDeactivateDeviceMutation();

  const handleConfirm = () => {
    let mutate = deactivateDevice;

    // Function to trigger the toast notification
    const triggerToastFn = (type: "success" | "error", content: string) => {
      // Set the toast type and content
      setToastType(type);
      setToastContent(content);

      // Close the modal after setting the toast
      props?.onClose();

      // Show the toast
      setShowToast(true);
    };

    // Determine the mutation based on the action type
    if (props?.actionType === "Activate") {
      mutate = activateDevice;
    }

    mutate({ deviceId: props?.deviceId })
      .then((response: any) => {
        if ('data' in response) {
          let content = t(LOCALE_CONST[`${props?.actionType.toLowerCase()}dSuccess`], { gatewayName: props?.deviceName });

          // Trigger the toast notification with success message
          triggerToastFn("success", content);

          // Call the refetch function if provided
          props?.refetchDeviceList?.();
        } else if ('error' in response) {
          const convertFn = (val: string) => t(val);

          const errContent = gatewayErrorStatusMessages(convertFn, t(LOCALE_CONST.unknownError), response.error.status, {
            401: t(LOCALE_CONST.unAuthFails),
            403: t(LOCALE_CONST.AuthForbidFails),
            404: t(LOCALE_CONST.projectIDNotFound),
            500: t(LOCALE_CONST.internalServerError)
          });

          triggerToastFn("error", errContent);
        }
      })
      .catch(() => {
        triggerToastFn("error", t(LOCALE_CONST.failedPerformAction));
      });
  };

  const displayTitle = () => {
    if (toastType === "success") {
      return t(LOCALE_CONST[`${props?.actionType.toLowerCase()}dSuccess`], { gatewayName: props?.deviceName });
    } else {
      return t(LOCALE_CONST[`${props?.actionType.toLowerCase()}Failed`]);
    }
  };

  const displaySubTitle = () => {
    if (toastType === "error") {
      return toastContent;
    } else {
      return "";
    }
  };

  return (
    <Fragment>
      <ConfirmationModal
        data-testid="confirmation-modal"
        isOpen={props?.isOpen}
        onClose={props?.onClose}
        onConfirm={handleConfirm}
        title={t(LOCALE_CONST[`${props?.actionType.toLowerCase()}Device`])}
        content={
          <div>
            <p>
              {`${t(LOCALE_CONST.deviceTypeConfirmation, { actionType: t(LOCALE_CONST[props?.actionType.toLowerCase()]) })} `}<strong>{props?.deviceName}</strong>{`?`}
            </p>
            {props?.actionType === "Deactivate" && (
              <p className="note">
                <div><InfoIcon className="info-icon" /></div>
                <span style={{ color: '#64C3FF' }}>{t(LOCALE_CONST.important)}:</span>
                <span className="important-text">{t(LOCALE_CONST.deactivateInfoNote)}</span>
              </p>
            )}
          </div>
        }
      />
      {showToast && (
        <div className="toast-container">
          <div onClick={() => setShowToast(false)} style={{ cursor: "pointer" }}>
            <Toast
              id={`${props?.actionType.toLowerCase()}-toast`}
              showClose={true}
              title={displayTitle()}
              type={toastType}
              content={""}
              subtitle={displaySubTitle()}
              showDismiss={false}
              data-testid="toast"
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default ActionModal;